import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo
} from "react";
import { debounce } from "lodash";

const TRESHOLDTORACECHANGE = 200;

export function useSetInView(
  refs: React.MutableRefObject<(HTMLDivElement | null)[]>,
  containerRef: React.MutableRefObject<HTMLDivElement | null>,
  defaultLegInView: number,
  isHorizontalScroll = false
) {
  const [legInView, setLegInView] = useState(defaultLegInView || 1);
  const [isScrolling, setScrolling] = useState(false);

  const currentLeg = useRef(defaultLegInView || 1);

  const handleEndScroll = useMemo(
    () => debounce(() => setScrolling(false), 300),
    []
  );

  const handleScrollListener = useCallback(() => {
    setScrolling(true);

    let containerOffsetStart = containerRef.current?.offsetTop ?? 0;
    let containerScrollPosition = containerRef.current?.scrollTop ?? 0;
    let containerClientSize = containerRef.current?.clientHeight ?? 0;
    let containerScrollSize = containerRef.current?.scrollHeight ?? 0;

    if (isHorizontalScroll) {
      containerOffsetStart = containerRef.current?.offsetLeft ?? 0;
      containerScrollPosition = containerRef.current?.scrollLeft ?? 0;
      containerClientSize = containerRef.current?.clientWidth ?? 0;
      containerScrollSize = containerRef.current?.scrollWidth ?? 0;
    }

    refs.current.forEach((ref, index) => {
      const elementOffsetStart =
        (isHorizontalScroll ? ref?.offsetLeft : ref?.offsetTop) ?? 0;

      const positionToStart =
        elementOffsetStart - containerOffsetStart - containerScrollPosition;

      if (
        positionToStart >= 0 &&
        positionToStart < TRESHOLDTORACECHANGE &&
        currentLeg.current !== index + 1
      ) {
        setLegInView(index + 1);
        currentLeg.current = index + 1;
      }

      if (
        containerScrollPosition < TRESHOLDTORACECHANGE &&
        currentLeg.current !== 1
      ) {
        setLegInView(1);
        currentLeg.current = 1;
      }

      if (
        containerScrollPosition + containerClientSize === containerScrollSize &&
        currentLeg.current !== refs.current.length
      ) {
        setLegInView(refs.current.length);
        currentLeg.current = refs.current.length;
      }
    });

    handleEndScroll();
  }, [isHorizontalScroll]);

  useEffect(() => {
    containerRef.current?.addEventListener("scroll", handleScrollListener);
    return () =>
      containerRef.current?.removeEventListener("scroll", handleScrollListener);
  }, [handleScrollListener]);

  return { legInView, isScrolling };
}

export default useSetInView;
